/*
 * Hubspot forms restyle
 *
 * Restyles Hubspot iframe forms. Injects our styles (based on ably-ui) into an iframe.
 */

// this is fine, the raw param is a vite thing
import hubspotFormsCss from './hubspot-forms.css?raw';

const WAIT_BETWEEN_RETRIES_MS = 300;

// Standalone function to fetch session data
const fetchSessionData = async (url: string) => {
  const res = await fetch(url, {
    headers: {
      accept: 'application/json',
    },
    cache: 'no-cache',
  });

  if (!res.ok) {
    throw new Error('Failed to fetch session data');
  }

  const contentType = res.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    throw new Error('Session endpoint is not serving json');
  }

  const data = await res.json();

  // Handle "not found" error gracefully
  if (data.error === 'not-found') {
    return null;
  }

  return data;
};

const restyleHubspotForm = (formIframeSelector: string) => {
  let retries = 10;

  const waitForIframe = () => {
    const iframe = document.querySelector(formIframeSelector);

    if (iframe) {
      const style = document.createElement('style');
      style.appendChild(document.createTextNode(`${hubspotFormsCss}\n`));
      const iframeElement = iframe as HTMLIFrameElement;
      if (iframeElement.contentDocument) {
        iframeElement.contentDocument.head.appendChild(style);
      }
    } else if (retries > 0) {
      retries -= 1;
      setTimeout(waitForIframe, WAIT_BETWEEN_RETRIES_MS);
    }
  };

  waitForIframe();
};

const setFormValues = ({
  firstname,
  lastname,
  email,
  session,
}: {
  firstname: JQuery<HTMLElement>;
  lastname: JQuery<HTMLElement>;
  email: JQuery<HTMLElement>;
  session: { user?: { firstName?: string; lastName?: string; email?: string } };
}) => {
  if (!session || !session.user) return;

  if (session.user?.firstName) firstname.val(session.user.firstName).change();
  if (session.user?.lastName) lastname.val(session.user.lastName).change();
  if (session.user?.email) email.val(session.user.email).change();
};

// form is a jQuery node
const hubspotOnFormReady = async (form: JQuery<HTMLElement>) => {
  const firstname = form.find('[name=firstname');
  const lastname = form.find('[name=lastname');
  const email = form.find('[name=email');

  const firstnameValue = firstname.val();
  const lastnameValue = lastname.val();
  const emailValue = email.val();

  // Skip if form already has values
  if (!!firstnameValue || !!lastnameValue || !!emailValue) return;

  // Fetch session data directly
  const sessionDataUrl = document.body.dataset.apiSessionData;
  if (sessionDataUrl) {
    try {
      const sessionData = await fetchSessionData(sessionDataUrl);
      setFormValues({ firstname, lastname, email, session: sessionData });
    } catch (error) {
      console.error('Failed to fetch session data for HubSpot form:', error);
    }
  }
};

// Resize an iframe according to dimentions of it's parent container
//
// This function returns a bound event handler
// e.g. window.addEventListener("resize", resizeIframeByContainer(".my-iframe", "#my-container"));
//
const resizeIframeByContainer = (iframeSelector: string, containerSelector: string) => {
  const iframe = document.querySelector(iframeSelector) as HTMLIFrameElement;
  const container = document.querySelector(containerSelector) as HTMLElement;

  if (!iframe || !container) throw new Error('Require both iframe and container DOM elements');

  return () => {
    const { clientWidth, clientHeight } = container;

    iframe.style.width = `${clientWidth}px`;
    iframe.style.height = `${clientHeight}px`;
  };
};

const createForm = (props: Record<string, unknown>) =>
  window.hbspt.forms.create({
    region: 'na1',
    portalId: '6939709',
    ...props,
  });

export { createForm, hubspotOnFormReady, restyleHubspotForm, resizeIframeByContainer };
